<script lang="ts">
  import type { ProjectConfig } from '../../modules/projects/project.interface';
  import type {
    CrawlerSource,
    CrawlerSourceSortingMap,
  } from '../../modules/crawler/crawler-source.interface';
  import type { Source } from '../../modules/source/source.interface';
  import { createEventDispatcher } from 'svelte';
  import { Dictionary, omit, pick, pickBy } from 'lodash';
  import { Radio, TextField, Button, Row, Col } from 'svelte-materialify';
  import { CrawlerSourceSorting } from '../../modules/crawler/crawler-source.interface';
  import {
    SourceCrawlerInterval,
    SourceCrawlerType,
  } from '../../modules/source/source.constants';
  import { CrawlerService, SourceService } from '../../modules';
  import { wrapProgress } from '../../store';
  import { UiError } from '../../lib/errors';
  import SearchSource from './SearchSource.svelte';
  import Select from '../Select.svelte';
  import ArticleToItemMapping from './ArticleToItemMapping.svelte';
  import SourceCard from './SourceCard.svelte';
  import ColorPicker from '../ColorPicker.svelte';

  export let projectConfig: ProjectConfig;
  export let source: Source;
  export let isEdit = false;

  const dispatch = createEventDispatcher();
  let crawlerSource: CrawlerSource;
  let sortings: CrawlerSourceSorting[];
  let articles: Dictionary<any>[];

  $: isSourceValid = validateSource(source);

  if (!isEdit) {
    source = SourceService.createDefaultSource(projectConfig.key);
  } else {
    wrapProgress(fetchFeed)();
  }

  async function fetchFeed() {
    const { type, feedKey, sorting, subsource } = source.crawlerOptions;

    if (type === SourceCrawlerType.INSTAGRAM) {
      const instagramSource = SourceService.createInstagramSource(subsource);

      crawlerSource = omit(instagramSource, 'articles');
      articles = instagramSource.articles;
      return;
    }
    crawlerSource = await CrawlerService.getSource(type, feedKey);
    articles = await CrawlerService.getArticles(type, feedKey, sorting, subsource);
  }

  function validateSource(source: Source) {
    const requiredProps = ['key', 'category', 'project', 'name'];
    const requiredCrawlerOptionsProps = ['interval', 'type', 'feedKey'];

    const isSourceValid = requiredProps.every((prop) => !!source[prop]);
    const isCrawlerOptionsValid =
      source.category === 'instagram'
        ? true
        : requiredCrawlerOptionsProps.every((prop) => !!source.crawlerOptions[prop]);

    return isSourceValid && isCrawlerOptionsValid;
  }

  function getSortings() {
    if (source.crawlerOptions.type === SourceCrawlerType.INSTAGRAM) {
      return [CrawlerSourceSorting.LATEST];
    }

    let sortingMap: CrawlerSourceSortingMap;

    if (source.crawlerOptions.subsource) {
      sortingMap = crawlerSource.subsources.find(
        (subsource) => subsource.name === source.crawlerOptions.subsource
      ).sorting;
    } else {
      sortingMap = crawlerSource.flags;
    }

    if (Object.values(sortingMap).every((isAvailable) => !isAvailable)) {
      if (!source.crawlerOptions.subsource && crawlerSource.subsources?.length) {
        return [];
      }

      return [CrawlerSourceSorting.LATEST];
    }

    const listTypesMap = pickBy(
      pick(sortingMap, Object.values(CrawlerSourceSorting)),
      (v: boolean) => !!v
    );

    return Object.keys(listTypesMap) as CrawlerSourceSorting[];
  }

  async function onSourceSelect(evt: { detail: CrawlerSource }) {
    const { name, title, icon, iconSVG, subsources } = evt.detail;

    crawlerSource = evt.detail;
    sortings = getSortings();

    if (source.crawlerOptions.type === SourceCrawlerType.INSTAGRAM) {
      const sourceKey = subsources[0].name;
      source.key = `instagram-${sourceKey}`;
      source.name = title;
      source.image = icon;
      source.crawlerOptions.sorting = sortings[0];
      source.crawlerOptions.feedKey = 'instagram';
      source.crawlerOptions.subsource = sourceKey;
      source.category = 'instagram';
    } else {
      if (!sortings.length && subsources?.length) {
        source.crawlerOptions.subsource = subsources[0].name;
        sortings = getSortings();
      }

      source.key = name;
      source.name = title;
      source.image = iconSVG || icon;
      source.crawlerOptions.sorting = sortings[0];
      source.crawlerOptions.feedKey = name;
      updateSourceKeyAndName();
    }

    const { type, sorting, subsource, feedKey } = source.crawlerOptions;

    try {
      articles =
        type === SourceCrawlerType.INSTAGRAM
          ? evt.detail.articles
          : await CrawlerService.getArticles(type, feedKey, sorting, subsource);

      if (!articles.length) {
        throw new UiError("Can't get articles!");
      }
    } catch (error) {
      if (!subsource && subsources.length) {
        await onSubsourceChange({ detail: subsources[0].name });
      }
    }
  }

  async function onSubsourceChange(evt: { detail: string }) {
    const selectedSubsource = evt.detail;
    source.crawlerOptions.subsource = selectedSubsource;
    sortings = getSortings();
    source.crawlerOptions.sorting = sortings[0];

    updateSourceKeyAndName();

    const { type, sorting, subsource, feedKey } = source.crawlerOptions;

    articles = await CrawlerService.getArticles(type, feedKey, sorting, subsource);

    if (!articles.length) {
      throw new UiError(`Can't get articles for ${subsource}!`);
    }
  }

  function updateSourceKeyAndName() {
    let name = source.name.split(' - ')[0];
    let key = source.key.split('-')[0];

    if (source.crawlerOptions.subsource) {
      const selectedSubsource = crawlerSource.subsources.find(
        (subsource) => subsource.name === source.crawlerOptions.subsource
      );
      name = `${name} - ${selectedSubsource.displayName}`;
      key = `${key}-${selectedSubsource.name}`;
    }

    source.name = name;
    source.key = key;
  }

  function createSource() {
    dispatch('create', source);
  }

  function updateSource() {
    dispatch('update', source);
  }

  function deleteSource() {
    dispatch('delete', source);
  }
</script>

<div class="source-create pa-8">
  {#if isEdit}
    <SourceCard {crawlerSource} className="single" />
  {:else}
    <div class="d-flex">
      {#each Object.values(SourceCrawlerType) as crawlerType}
        <Radio
          style="text-transform: capitalize; margin: 0 32px 24px 0;"
          bind:group={source.crawlerOptions.type}
          value={crawlerType}>{crawlerType}</Radio
        >
      {/each}
    </div>
    <SearchSource
      type={source.crawlerOptions.type}
      on:select={wrapProgress(onSourceSelect)}
    />
  {/if}
  {#if articles?.length}
    <Row>
      {#if crawlerSource.subsources?.length && source.crawlerOptions.type !== SourceCrawlerType.INSTAGRAM}
        <Col>
          <Select
            className="text-capitalize"
            items={crawlerSource.subsources}
            displayKey="displayName"
            bindKey="name"
            on:select={wrapProgress(onSubsourceChange)}
            bind:selectedItem={source.crawlerOptions.subsource}>Subsource</Select
          >
        </Col>
      {/if}
      <Col>
        <Select
          className="text-capitalize"
          items={sortings}
          bind:selectedItem={source.crawlerOptions.sorting}>Sorting</Select
        >
      </Col>
      <Col>
        <Select
          items={projectConfig.categories}
          displayKey="name"
          bindKey="key"
          bind:selectedItem={source.category}>Category</Select
        >
      </Col>
    </Row>
  {/if}
  {#if source.category && articles?.length}
    <ArticleToItemMapping {source} {articles} bind:mapping={source.crawlerMapping} />
    <Row>
      <Col>
        <TextField bind:value={source.name}>* Source Name</TextField>
        <h5 class="text-body-1 mt-2">Source Color</h5>
        <ColorPicker bind:color={source.color} />
      </Col>
      <Col>
        <TextField bind:value={source.url}>* Source URL</TextField>
        <Select
          className="mt-7"
          items={Object.values(SourceCrawlerInterval)}
          bind:selectedItem={source.crawlerOptions.interval}>Crawler Interval</Select
        >
      </Col>
    </Row>
  {/if}
  <div class="mt-8 mb-8">
    {#if isEdit}
      {#if source.category !== 'instagram'}
        <Button
          class={isSourceValid ? 'amber' : ''}
          disabled={!isSourceValid}
          on:click={() => updateSource()}>Save</Button
        >
      {/if}
      <Button class="red white-text" on:click={() => deleteSource()}>Delete</Button>
    {:else}
      <Button
        class={isSourceValid ? 'green white-text' : ''}
        disabled={!isSourceValid}
        on:click={() => createSource()}>Add Source</Button
      >
    {/if}
  </div>
</div>

<style>
  .source-create {
    padding-bottom: 200px;
  }
</style>
